import { useEffect, useState } from 'react'
import {
  Button,
  HStack,
  Box,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack
} from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import set from 'lodash/set'
import { observer } from 'mobx-react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useLocation } from 'react-router-dom'
import { primary } from 'theme/globalStyles'
import { IOption } from 'types/common'
import { AuthRoleIdEnum } from 'constants/user'
import { ITheme } from 'interfaces/theme'
import routes from 'routes'
import { ISearchFilterForm } from 'stores/searchStore'
import { getValidArray } from 'utils/common'
import FilterInput from 'components/Pages/Common/FilterInput'
import Dropdown from 'components/Dropdown'
import { IDropdown } from 'types/common/select'
import { searchSortByOptions } from 'components/Topbar/constants'
import PrimaryButton from 'components/PrimaryButton'
import {
  DARK_MODAL_CANCEL_BUTTON,
  DARK_MODAL_CONTENT,
  DARK_MODAL_OVERLAY,
  DARK_TABLE_BORDER,
  DARK_TEXT_PRIMARY
} from 'constants/dark-tokens'

const defaultValues: ISearchFilterForm = {
  groups: [],
  sort: ''
}

interface IFilterModalProps {
  isOpen: boolean
  onClose: () => void
  // Opt-in dark-surface styling (default light).
  dark?: boolean
}

const FilterModal = (props: IFilterModalProps) => {
  const { isOpen, onClose, dark = false } = props
  const { organizationStore, authStore, userStore, searchStore, groupStore } = useStores()
  const { tagStore } = useStores()
  const { userDetail } = authStore
  const { organization } = organizationStore
  const { currentUserGroupMembers } = userStore
  const { searchFilter } = searchStore
  // Same fallback as SearchPageHeader: organization is null off-subdomain.
  const organizationId: number = organization?.id ?? authStore.resolvedActiveOrgId ?? 0
  const currentTheme: ITheme = organization?.theme ?? {}
  const userGroupIds = currentUserGroupMembers.map(groupMember => groupMember.groupId)
  const isBasicUser = userDetail.authRoleId === AuthRoleIdEnum.PLAYER
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const [selectedGroups, setSelectedGroups] = useState<IOption<string>[]>([])
  const [selectedTags, setSelectedTags] = useState<IOption<string>[]>([])
  const [selectedSort, setSelectedSort] = useState<IDropdown | null>(null)

  const methods = useForm<ISearchFilterForm>({
    defaultValues,
    mode: 'onChange',
    resolver: async data => {
      return {
        values: data,
        errors: {}
      }
    }
  })
  const { handleSubmit, reset, setValue } = methods

  function handleSelectGroups(options: IOption<string>[]): void {
    const groups = getValidArray(options).map(option => {
      return { label: option?.label, value: Number(option?.value) }
    })
    setValue('groups', groups)
    setSelectedGroups(options)
  }

  function handleSelectTags(options: IOption<string>[]): void {
    const tags = getValidArray(options).map(option => {
      return { label: option?.label, value: Number(option?.value) }
    })
    setValue('tags', tags as any)
    setSelectedTags(options)
  }

  function handleChangeSort(name: string, dropdownValue: IDropdown): void {
    setSelectedSort(dropdownValue)
    setValue('sort', String(dropdownValue?.value ?? ''))
  }

  async function onSubmit(data: ISearchFilterForm) {
    try {
      searchStore.changeSearchFilter(data)
      params.set('page', '1')
      params.set('filter', `${JSON.stringify(data)}`)
      if (getValidArray(data?.groups)?.length > 0) {
        searchStore.changeGroupIds(getValidArray(data?.groups).map(group => Number(group.value)) || [])
      }
      onClose()
      navigate(`${routes.search.value}?${params.toString()}`)
      await searchStore.search()
    } catch (error: any) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (isOpen) {
      const filterGroup = {
        where: { organizationId }
      }
      if (isBasicUser) {
        set(filterGroup, 'where.id', { inq: userGroupIds })
      }
      groupStore.getGroups(filterGroup)
      reset(searchFilter)
      setSelectedGroups(
        getValidArray(searchFilter?.groups).map(g => ({ label: String(g?.label ?? ''), value: String(g?.value ?? '') }))
      )
      setSelectedTags(
        getValidArray(searchFilter?.tags).map(t => ({ label: String(t?.label ?? ''), value: String(t?.value ?? '') }))
      )
      setSelectedSort(
        searchFilter?.sort
          ? { title: String(searchFilter.sort), value: String(searchFilter.sort) }
          : null
      )
      tagStore.fetchTags({ where: { organizationId }, order: ['name ASC'] })
    }
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} id="search-form">
          <ModalOverlay {...(dark ? DARK_MODAL_OVERLAY : {})} />
          <ModalContent
            minWidth={{ base: 'full', md: '800px' }}
            borderRadius={8}
            {...(dark ? DARK_MODAL_CONTENT : {})}
          >
            <ModalHeader fontSize="lg" fontWeight={500} lineHeight={7} color={dark ? DARK_TEXT_PRIMARY : 'gray.800'}>
              More filters
            </ModalHeader>
            <ModalCloseButton
              background={dark ? 'transparent' : 'white'}
              border="none"
              boxShadow="none !important"
            />
            <ModalBody border={`1px solid ${dark ? DARK_TABLE_BORDER : '#E2E8F0'}`} padding={6}>
            <Box display={{ base: 'block', md: 'none' }} >
                <Box width="full">
                  <Text color={dark ? DARK_TEXT_PRIMARY : 'gray.700'} fontSize="16px" fontWeight={500} mb={2}>
                    Sort By
                  </Text>
                  <Dropdown
                    name="sort"
                    titleDropdown="SORT BY"
                    fontSize="md"
                    width={{ base: 'full' }}
                    height="40px"
                    options={searchSortByOptions}
                    item={{
                      title: selectedSort?.title ?? (searchFilter.sort ? String(searchFilter.sort) : 'Most relevant'),
                      value: selectedSort?.value ?? (searchFilter.sort ? String(searchFilter.sort) : 'Most relevant')
                    }}
                    setValue={handleChangeSort}
                    isDisabled={false}
                    isMatchWidth={true}
                    dark={dark}
                  />
                </Box>
                <Box borderTop={`1px solid ${dark ? DARK_TABLE_BORDER : '#E2E8F0'}`} mt={6} mb={6} />
                <VStack width="full" gap={0} alignItems="flex-start">
                  <FilterInput
                    isOpenModal={isOpen}
                    name="tags"
                    label="Tag"
                    placeholder="Search tags"
                    storeOptions={getValidArray(tagStore.tags)}
                    filteredOptions={getValidArray(searchFilter?.tags)}
                    selectedOptions={selectedTags}
                    setSelectedOptions={handleSelectTags}
                    dark={dark}
                  />
                </VStack>
              </Box>
              <HStack width="full" alignItems="flex-start" spacing={6}>
                <FilterInput
                  isOpenModal={isOpen}
                  name="groups"
                  label="Group"
                  placeholder="Search groups by name"
                  storeOptions={getValidArray(groupStore.groups)}
                  filteredOptions={getValidArray(searchFilter?.groups)}
                  selectedOptions={selectedGroups}
                  setSelectedOptions={handleSelectGroups}
                  dark={dark}
                />
              </HStack>
            </ModalBody>
            <ModalFooter>
              <HStack width="full" justify="space-between" alignItems="center">
                <Text
                  color={dark ? DARK_TEXT_PRIMARY : 'gray.700'}
                  fontSize="16px"
                  fontWeight={500}
                  lineHeight={6}
                  cursor="pointer"
                  display={{ base: 'block', md: 'none' }}
                  onClick={() => {
                    searchStore.clearSearchFilter()
                    setSelectedGroups([])
                    setSelectedTags([])
                    setSelectedSort(null)
                    reset({ ...searchFilter, groups: [], tags: [] as any, sort: '' })
                  }}
                >
                  Clear filters
                </Text>
                {/* On the dark surface the pair is the same Cancel/primary pair every
                    other RallyPD modal uses, so this footer cannot drift from them. */}
                <HStack gap={dark ? 3 : 2}>
                  {dark ? (
                    <Button {...DARK_MODAL_CANCEL_BUTTON} onClick={onClose}>
                      Cancel
                    </Button>
                  ) : (
                    <Button variant="outline" background="white" fontWeight={500} lineHeight={6} onClick={onClose}>
                      Cancel
                    </Button>
                  )}
                  {dark ? (
                    <PrimaryButton type="submit" form="search-form">
                      Apply
                    </PrimaryButton>
                  ) : (
                    <Button
                      type="submit"
                      form="search-form"
                      variant="outline"
                      color="white"
                      fontWeight={500}
                      lineHeight={6}
                      background={currentTheme?.primaryColor ?? primary}
                      _hover={{ opacity: 0.8 }}
                    >
                      Apply
                    </Button>
                  )}
                </HStack>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  )
}

export default observer(FilterModal)
